import { useEffect, useRef } from 'react'

import { setOptimizelyExperiment } from 'actions/SystemActions'
import { isReady, preloaders, userContext } from 'analytics/optimizely/optimizely'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import createSelector from 'lib/createSelector'
import { upsertContext, Tracker } from 'analytics/analytics'

export type OptimizelyExperiments = OptimizelyClientSideExperiments | OptimizelyServerSideExperiments;

export enum OptimizelyClientSideExperiments {
  lereStickyAbandonedCartEnabled = 'lere_sticky_ac_a_b_test',
  lereSimilarOffersGraphModelEnabled = 'lere_similar_offers_reco_using_graph_model_on_offer_page',
  lereLpcLowRpvFooterEnabled = 'lere_lpc_sticky_footer_low_rpv',
  searchDynamicEv = 'search_dynamic_ev_with_ctr_test_ab',
  searchPriceMapMarkers = 'search_price_map_markers',
  searchNightlyPriceMapMarkers = 'search_nightly_price_map_markers',
  searchShowLoggedOutPrices = 'search_show_logged_out_prices',
  isTourReviewsEnabled = 'cro_tours_reviews',
  isNortonButtonEnabled = 'cro_norton_button',
  clientSideExperimentAATest = 'aa_test_client_18_10',
  tripOfferPageSaveButton = 'trip_save_button_offer_page',
  croCheckoutPayButton = 'cro_checkout_pay_button_cta',
  tripSearchPageSaveButton = 'trip_save_button_search_page',
  paymentsMerchantFees = 'payments_merchant_fees',
  tripRememberTripToSaveTo = 'trip_remember_trip_to_save_to',
}

export enum OptimizelyServerSideExperiments {
  isNewHomePageTestEnabled = 'cro_new_homepage_v2',
  isAuNewHomePageTestEnabled = 'cro_new_homepage_v2_au',
  isNewLandingPageTestEnabled = 'cro_new_landing_page',
  isSupportAssistantEnabled = 'opex_support_assistant_customer_pool',
  serverSideExperimentAATest = 'aa_test_server_18_10',
  isPersonalisedRecCalendar = 'lere_personalised_recos_in_calendar_with_offer_id',
  lereTpfmCarouselEnabledOnHotelsPage = 'lere_hotels_vertical_personalised_alternatives_test',
  lereTpfmCarouselEnabledOnHomePage = 'lere_home_page_personalised_alternatives_test',
  searchNewHeader = 'search_new_header',
  paymentsPayToRelease = 'payments_payto_release',
  searchNewMapTilesAndHeader = 'search_new_map_tiles_and_header',
  searchBedbankSellPrice = 'search_bedbank_sell_price',
}

export function activateOptimizelyExperiment(flagKey: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) {
  // This function shouldn't be called on server
  if (typeof window === 'undefined') {
    return
  }

  // datafile can take a few seconds to load, if it's not ready, add the flag to the preloaders array
  if (!isReady) {
    preloaders.push(flagKey)
    return
  }
  if (!userContext) return

  try {
    const decision = userContext.decideForKeys([flagKey])[flagKey]
    const experimentValues = {
      variationKey: decision.variationKey,
      variables: decision.variables,
      enabled: decision.enabled,
    }

    // update snowplow user context with ab test data.
    // Need to filter out the homepage test as it is already being tracked a different way and we don't want to change it halfway through the test.
    const homepageTest =
      flagKey === OptimizelyServerSideExperiments.isNewHomePageTestEnabled ||
      flagKey === OptimizelyServerSideExperiments.isAuNewHomePageTestEnabled

    // moved code from initialiseAnalytics to here
    if (homepageTest) {
      const homePageVariationKey = decision?.enabled ? decision.variationKey : undefined

      upsertContext(Tracker.snowplow, 'experiments', {
        key: OptimizelyServerSideExperiments.isNewHomePageTestEnabled,
        value: homePageVariationKey ? 'new-homepage' : 'old-homepage',
      },
      )
    } else {
      upsertContext(Tracker.snowplow, 'experiments', {
        key: flagKey,
        value: decision.variationKey || '',
      },
      )
    }
    return experimentValues
  } catch (error) {
    console.error(error)
  }
}

/**
 * Optimizely Experiments Selectors.  Each experiment can be found in Redux in state.config.optimizelyExperiments.
 * Each has an 'enabled' value that can be used to determine if the user is enrolled in a variant (if the user is
 * part of the control, or the test isn't live in Optimizely, the value will be false).  Each experiment also has
 * a 'variationKey' value that can be used to determine which variant the user is enrolled in (think of this as
 * the same thing as the number variant previously used in Optimize).  Each experiment also has a 'variables'value
 * that can be used to pass variables from Optimizely to use in the experiment.
 *
 * Return the optimizely experiment variant dynamically based on the given trigger boolean condition.
 * If canTriggerExperiment is false the experiment will not be activated.
 * Note: once the trigger becomes true once, it will be enabled for the rest of the session until page refresh.
 *
 * @param canTriggerExperiment a boolean indicating whether the experiment can be triggered
 * @param experimentId experiment id from the Optimizely UI (Rule Key)
 * @returns Experiment variant if can be triggered
 */
export function useOptimizelyExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments, canTriggerExperiment: boolean = true) {
  const dispatch = useAppDispatch()
  const experimentActivated = useRef(false)
  useEffect(() => {
    if (canTriggerExperiment && !experimentActivated.current) {
      dispatch(setOptimizelyExperiment(experimentId))
      experimentActivated.current = true
    }
  }, [dispatch, canTriggerExperiment, experimentId])

  const experimentVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, experimentId))
  if (canTriggerExperiment) { return experimentVariation }
}

export function isClientSideExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments): experimentId is OptimizelyClientSideExperiments {
  return Object.values(OptimizelyClientSideExperiments).includes(experimentId as OptimizelyClientSideExperiments)
}

export const getOptimizelyExperimentVariation = createSelector(
  (state: App.State, experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) => state.config.optimizelyExperiments[experimentId],
  (experiment) => {
    if (!experiment || !experiment.enabled) {
      return
    }
    if (experiment.enabled && Object.keys(experiment.variables).length) {
      return experiment.variables[experiment.variationKey]
    }
    return experiment.variationKey
  },
)
